import type { DocumentData, DocumentSnapshot, QuerySnapshot } from 'firebase/firestore'

import { callable } from '@/lib/firebase/callable'
import { downloadStorageBytes } from '@/lib/firebase/storage'
import { type Subscription, subscribeCollection } from '@/lib/firebase/subscribe'
import { parseNdjson } from '@/lib/ndjson'
import {
  type SetupScope,
  asIsoOrNull,
  asNumber,
  asOneOf,
  asRecords,
  asString,
  asStringOrNull,
  isRecord,
  setupCollection,
  subscribeLatestRun,
  toRunEntry,
  toSetupRun,
} from '@/repositories/setup-docs'
import { SEARCH_INTENTS, type SearchIntent } from '@/types/search-intent'
import {
  SEARCH_SNAPSHOT_ROLES,
  SEARCH_SNAPSHOT_SCOPES,
  SEARCH_SNAPSHOT_STAGES,
  type SearchSnapshotDomainEntry,
  type SearchSnapshotObject,
  type SearchSnapshotPage,
  type SearchSnapshotRow,
  type SearchSnapshotRun,
  type SearchSnapshotSummary,
} from '@/types/search-snapshot'

function toDomainEntry(entry: DocumentData): SearchSnapshotDomainEntry | undefined {
  const domain = asString(entry.domain)
  if (domain === '') return undefined
  return {
    ...toRunEntry(entry),
    domain,
    role: asOneOf(entry.role, SEARCH_SNAPSHOT_ROLES, 'rival'),
    ...(typeof entry.rows === 'number' ? { rows: entry.rows } : {}),
  }
}

function toSearchSnapshotRun(
  snapshot: DocumentSnapshot<DocumentData>,
): SearchSnapshotRun | undefined {
  const data = snapshot.data()
  if (!data) return undefined
  return {
    ...toSetupRun(snapshot.id, data, SEARCH_SNAPSHOT_STAGES, toDomainEntry),
    scope: asOneOf(data.scope, SEARCH_SNAPSHOT_SCOPES, 'all'),
    startedByUserId: typeof data.startedByUserId === 'string' ? data.startedByUserId : null,
    ...(typeof data.tailErrorCode === 'string' ? { tailErrorCode: data.tailErrorCode } : {}),
  }
}

export function subscribeLatestSearchSnapshotRun(
  scope: SetupScope,
): Subscription<SearchSnapshotRun | null> {
  return subscribeLatestRun(scope, 'searchSnapshotRuns', toSearchSnapshotRun)
}

export interface RefreshSearchSnapshotResult {
  runId: string
}

const refreshSearchSnapshotCallable = callable<Record<string, never>, RefreshSearchSnapshotResult>(
  'refreshSearchSnapshot',
)

export async function refreshSearchSnapshot(): Promise<RefreshSearchSnapshotResult> {
  return refreshSearchSnapshotCallable({})
}

const SEARCH_IDEAS_DOC_ID = 'ideas'

const SEARCH_SNAPSHOT_SCHEMA_VERSION = 1

function asFiniteOrNull(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

function toSummary(id: string, data: DocumentData): SearchSnapshotSummary | undefined {
  const domain = asString(data.domain)
  const objectPath = asString(data.objectPath)
  const runId = asString(data.runId)
  const fetchedAt = asIsoOrNull(data.fetchedAt)
  if (domain === '' || objectPath === '' || runId === '' || fetchedAt === null) return undefined
  return {
    domain,
    domainKey: asStringOrNull(data.domainKey) ?? id,
    competitorId: asStringOrNull(data.competitorId),
    role: asOneOf(data.role, SEARCH_SNAPSHOT_ROLES, 'rival'),
    runId,
    fetchedAt,
    rowCount: asNumber(data.rowCount),
    pageCount: asNumber(data.pageCount),
    objectPath,
  }
}

function toSummaries(snapshot: QuerySnapshot<DocumentData>): SearchSnapshotSummary[] {
  return snapshot.docs.flatMap((docSnapshot) => {
    if (docSnapshot.id === SEARCH_IDEAS_DOC_ID) return []
    const summary = toSummary(docSnapshot.id, docSnapshot.data())
    return summary ? [summary] : []
  })
}

export function subscribeSearchSnapshotSummaries(
  scope: SetupScope,
): Subscription<SearchSnapshotSummary[]> {
  return subscribeCollection(setupCollection(scope, 'searchSnapshot'), toSummaries)
}

function toHistory(value: unknown): SearchSnapshotRow['history'] {
  if (!isRecord(value) || !isRecord(value.from) || !Array.isArray(value.values)) return null
  const y = asFiniteOrNull(value.from.y)
  const m = asFiniteOrNull(value.from.m)
  if (y === null || m === null) return null
  return {
    from: { y, m },
    values: value.values.filter(
      (entry): entry is number => typeof entry === 'number' && Number.isFinite(entry),
    ),
  }
}

function asIntentOrNull(value: unknown): SearchIntent | null {
  return SEARCH_INTENTS.find((intent) => intent === value) ?? null
}

function toRow(value: unknown): SearchSnapshotRow | undefined {
  if (!isRecord(value)) return undefined
  const keyword = asString(value.keyword)
  const position = asFiniteOrNull(value.position)
  if (keyword === '' || position === null) return undefined
  return {
    keyword,
    coreKeyword: asStringOrNull(value.coreKeyword),
    volume: asFiniteOrNull(value.volume),
    difficulty: asFiniteOrNull(value.difficulty),
    intent: asIntentOrNull(value.intent),
    history: toHistory(value.history),
    position,
    url: asStringOrNull(value.url),
    relativeUrl: asStringOrNull(value.relativeUrl),
    etv: asFiniteOrNull(value.etv),
    citesAiOverview: value.citesAiOverview === true,
    hasAiOverview: value.hasAiOverview === true,
  }
}

function toPage(value: DocumentData): SearchSnapshotPage | undefined {
  const url = asString(value.url)
  if (url === '') return undefined
  return { url, etv: asFiniteOrNull(value.etv), count: asFiniteOrNull(value.count) }
}

/** Maps the parsed lines of a stored snapshot object; `undefined` when the header is missing, of another schema version, or names no domain. */
export function toSearchSnapshotObject(
  lines: readonly unknown[],
): SearchSnapshotObject | undefined {
  const [header, ...rows] = lines
  if (!isRecord(header) || header.schemaVersion !== SEARCH_SNAPSHOT_SCHEMA_VERSION) return undefined
  const domain = asString(header.domain)
  if (domain === '') return undefined
  return {
    domain,
    competitorId: asStringOrNull(header.competitorId),
    role: asOneOf(header.role, SEARCH_SNAPSHOT_ROLES, 'rival'),
    runId: asString(header.runId),
    fetchedAt: asString(header.fetchedAt),
    pages: asRecords(header.pages)
      .map(toPage)
      .filter((page): page is SearchSnapshotPage => page !== undefined),
    rows: rows.map(toRow).filter((row): row is SearchSnapshotRow => row !== undefined),
  }
}

/** Reads one domain's stored snapshot object once; `undefined` when it cannot be read as one. */
export async function readSearchSnapshotObject(
  objectPath: string,
): Promise<SearchSnapshotObject | undefined> {
  const bytes = await downloadStorageBytes(objectPath)
  return toSearchSnapshotObject(await parseNdjson(bytes))
}
